/**
PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 *  Copyright © 2025 Pearson Education, Inc.
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
* */

/**
 * TabularStyles
 *
 * @file TabularStyles.jsx
 * @author Mohamed yasar arabath M
 * @since 1.0.0
 */

import {
  ButtonBase,
  Table,
  TableRow,
  TableCell,
  Typography,
  Paper,
  TableContainer,
  IconButton,
  Checkbox,
  Button,
  Chip,
  Select,
  MenuItem,
  MenuList,
  LinearProgress
} from '@mui/material';
import { styled } from '@mui/material/styles';
import Icon from '../../icons/Icon';
import mixins from '../../../assets/styles/mixins.scss';
import CommonStyles from '../../../assets/styles/CommonStyles';
import UnderlineButton from '../../common/UnderlineTextButton ';

const {
  subtitle2
} = CommonStyles();

export const PaddingCheckbox = styled(Checkbox)({
  '&&': {
    padding: '0px',
    color: `${mixins.amethyst} !important`,
    '& svg': {
      width: 20,
      height: 20
    },
    '&.Mui-disabled': {
      '& svg path': {
        fill: '#E6E6E6',
        stroke: '#CCCCCC',
        strokeWidth: 2
      },

      '& svg rect': {
        fill: '#E6E6E6',
        stroke: '#CCCCCC',
        strokeWidth: 2
      }
    }
  }
});

export const HeaderPaddingCheckbox = styled(PaddingCheckbox)({
  '&&': {
    padding: '0 8px 0 0'
  }
});

export const Root = styled(Paper)({
  '&&': {}
});

export const TableContainerRoot = styled(TableContainer)({
  '&&': {
    maxHeight: 'var(--table-container-max-height)',
    overflow: 'hidden',

    /**
     * Card row variant (config.rowVariant = 'card').
     * Rows read as separate rounded cards floating on a tinted panel.
     */
    '&.cardTableWrapper': {
      backgroundColor: mixins.lightThemeBgColor2,
      borderRadius: 12,
      padding: '8px 0'
    }
  }
});

export const TabularTable = styled(Table)({
  '&&': {
    '--table-head-padding-top': 'var(--table-container-head-padding-top, 0px)',
    '--table-head-padding-left': 'var(--table-container-head-padding-left, 0px)',
    '--table-head-padding-right': 'var(--table-container-head-padding-right, 0px)',
    '--table-head-padding-bottom': 'var(--table-container-head-padding-bottom, 12px)',
    '--table-cell-padding-top': 'var(--table-container-cell-padding-top, 8px)',
    '--table-cell-padding-bottom': 'var(--table-container-cell-padding-bottom, 8px)',
    '--table-cell-padding-left': 'var(--table-container-cell-padding-left, 0px)',
    '--table-cell-padding-right': 'var(--table-container-cell-padding-right, 0px)',
    '--table-cell-gap': 'var(--table-container-cell-gap, 12px)',

    '& tbody tr:last-child td': {
      borderBottom: 'none !important'
    },

    '&.cardTable': {
      borderCollapse: 'separate',
      borderSpacing: '0 var(--table-card-row-gap, 4px)'
    }
  }
});

export const TableBodyRowRoot = styled(TableRow)({
  '&&': {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: 'rgba(13, 0, 77, 0.15)',
      '& .editActionIcon': {
        visibility: 'visible'
      }
    },
    '&.errorRow': {
      backgroundColor: mixins.accentLightRed,
      '& td': {
        borderBottom: '1px solid rgba(13, 0, 77, 0.15)'
      },
      '&:hover': {
        backgroundColor: mixins.accentLightRed
      }
    },
    '&.tableBodyRowLast': {
      '& td': {
        borderBottom: 'none !important'
      }
    },
    '&.disabledRow': {
      cursor: 'not-allowed',
      pointerEvents: 'none',
      '&:hover': {
        cursor: 'not-allowed',
        backgroundColor: 'transparent'
      }
    },
    '&.cardBodyRowRoot': {
      '&:hover': {
        backgroundColor: 'transparent'
      },
      '& > .cardCell': {
        backgroundColor: mixins.chalkWhiteFont,
        border: 'none',
        transition: 'background-color 0.15s ease, box-shadow 0.15s ease'
      },
      '& > .cardCell:first-child': {
        borderTopLeftRadius: 12,
        borderBottomLeftRadius: 12
      },
      '& > .cardCell:last-child': {
        borderTopRightRadius: 12,
        borderBottomRightRadius: 12
      },
      /**
       * The active row reads as a highlighted card. Inset shadows keep the row
       * exactly the same height as its resting state (a real border would not).
       */
      '&&:hover:not(.disabledRow) > .cardCell, &&.selectedRow:not(.disabledRow) > .cardCell': {
        backgroundColor: `${mixins.feedBackColor} !important`,
        boxShadow: 'inset 0px 2px 0px #8E84DC, inset 0px -2px 0px #8E84DC'
      },
      '&&:hover:not(.disabledRow) > .cardCell:first-child, &&.selectedRow:not(.disabledRow) > .cardCell:first-child': {
        boxShadow: 'inset 0px 2px 0px #8E84DC, inset 0px -2px 0px #8E84DC, inset 2px 0px 0px #8E84DC'
      },
      '&&:hover:not(.disabledRow) > .cardCell:last-child, &&.selectedRow:not(.disabledRow) > .cardCell:last-child': {
        boxShadow: 'inset 0px 2px 0px #8E84DC, inset 0px -2px 0px #8E84DC, inset -2px 0px 0px #8E84DC'
      }
    }
  }
});

export const TableHeadRowRoot = styled(TableRow)({
  '&&': {
    '& th': {
      backgroundColor: 'unset',
      fontFeatureSettings: '\'liga\' off',
      fontFamily: mixins.fontFamilyPlusSans,
      fontSize: mixins.fontSize12,
      fontStyle: 'normal',
      fontWeight: mixins.fontWeight500,
      lineHeight: '166.667%',
      padding: '0px',
      paddingBottom: 'var(--table-head-padding-bottom)',
      paddingTop: 'var(--table-head-padding-top)',
      paddingLeft: 'var(--table-head-padding-left)',
      paddingRight: 'var(--table-head-padding-right)',
      color: mixins.greyScaleColor
    },
    '&.cardHeaderRowRoot': {
      '& th': {
        backgroundColor: 'transparent',
        fontFeatureSettings: '\'liga\' off',
        fontFamily: mixins.fontFamilyPlusSans,
        fontSize: mixins.fontSize12,
        fontStyle: 'normal',
        fontWeight: mixins.fontWeight500,
        lineHeight: '14px',
        color: mixins.greyScaleColor,
        borderBottom: 'none',
        // 12px here + the 4px row gap gives the 16px the design puts under the header
        padding: '0px 8px 12px'
      }
    }
  }
});

export const TitleContainer = styled('div')({
  '&&': {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    width: '100%'
  }
});

export const TitleContainerLeft = styled('div')({
  '&&': {
    flexShrink: 0
  }
});

export const TitleContainerRight = styled('div')({
  '&&': {
    flex: 1,
    marginBottom: 20
  }
});

export const Primary = styled('div')({
  '&&': {
    display: 'flex',
    alignItems: 'center',
    '&.primaryGap': {
      gap: 8
    }
  }
});

export const PrimaryCol1 = styled('div')({
  '&&': {
    display: 'flex',
    padding: '0 8px',
    alignItems: 'flex-start',
    flex: '1 0 0',
    flexDirection: 'column'
  }
});

export const Subtitle2 = styled(Typography)({
  '&&': {
    ...subtitle2,
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize14,
    lineHeight: mixins.lineHeight22,
    '& p': {
      color: mixins.color_text_primary,
      fontFeatureSettings: '\'liga\' off',
      fontFamily: mixins.fontFamilyPlusSans,
      fontSize: mixins.fontSize14,
      fontStyle: 'normal',
      fontWeight: mixins.fontWeight600,
      lineHeight: mixins.lineHeight22
    }
  }
});

export const Subtitle2LineHeight24 = styled(Typography)({
  '&&': {
    ...subtitle2,
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize14,
    lineHeight: mixins.lineHeight24,
    '& p': {
      color: mixins.color_text_primary,
      fontFeatureSettings: '\'liga\' off',
      fontFamily: mixins.fontFamilyPlusSans,
      fontSize: mixins.fontSize16,
      fontStyle: 'normal',
      fontWeight: mixins.fontWeight600,
      lineHeight: mixins.lineHeight24
    }
  }
});

export const Caption = styled(Typography)({
  '&&': {
    color: mixins.zambezi,
    fontFeatureSettings: '\'liga\' off',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize12,
    fontStyle: 'normal',
    fontWeight: mixins.fontWeight400,
    lineHeight: '166.667%'
  }
});

export const Body2 = styled(Typography)({
  '&&': {
    color: mixins.navyColor,
    fontFeatureSettings: '\'liga\' off',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize14,
    fontStyle: 'normal',
    fontWeight: mixins.fontWeight500,
    lineHeight: '157.143%'
  }
});

export const TextPrimaryBody2 = styled(Body2)({
  '&&': {
    color: mixins.color_text_primary
  }
});

export const AmethystColor = styled(Body2)({
  '&&': {
    color: mixins.amethystColor,
    marginLeft: 8
  }
});

export const AttemptedBar = styled(LinearProgress)({
  '&&': {
    height: 6,
    borderRadius: 4,
    width: 164,
    backgroundColor: mixins.amethystLightColor
  }
});

export const CorrectBar = styled('div')({
  '&&': {
    height: 8,
    borderRadius: 4,
    backgroundColor: '#AE77FC'
  }
});

export const ChipLabel = styled(Typography)({
  '&&': {
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize12,
    fontWeight: mixins.fontWeight500,
    lineHeight: '20px',
    paddingLeft: '6px !important',
    paddingRight: '6px !important'
  }
});

export const ChipMathText = styled(Typography)({
  '&&': {
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize12,
    fontWeight: mixins.fontWeight500,
    lineHeight: '20px'
  }
});

export const ChipStyled = styled(Chip)({
  '&&': {
    maxHeight: 20,
    margin: '0 0px 2px 0px',
    fontWeight: 500,
    backgroundColor: mixins.feedBackColor,
    color: mixins.accentRed,
    borderRadius: 4,
    padding: '0px 2px',
    '& .MuiChip-label': {
      fontFamily: mixins.fontFamilyPlusSans,
      fontSize: mixins.fontSize12,
      fontWeight: mixins.fontWeight500,
      lineHeight: '20px',
      paddingLeft: '6px !important',
      paddingRight: '6px !important'
    }
  }
});

export const CheckIcon = styled('span')({
  '&&': {
    color: '#37B866',
    verticalAlign: 'middle',
    marginRight: 4
  }
});

export const ErrorIconStyled = styled('span')({
  '&&': {
    color: '#FFAB00',
    verticalAlign: 'middle',
    marginRight: 4
  }
});

export const LinkButton = styled(UnderlineButton)({
  '&&': {
    color: mixins.color_text_primary,
    textDecoration: 'none',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize16,
    fontStyle: 'normal',
    fontWeight: mixins.fontWeight600,
    lineHeight: '24px'
  },
  '&&:hover': {
    color: mixins.linkDefault,
    textDecoration: 'underline'
  }
});

export const HeaderTableCell = styled(TableCell)({
  '&&': {
    paddingTop: 'var(--table-cell-padding-top)',
    paddingBottom: 'var(--table-cell-padding-bottom)',
    paddingLeft: 'var(--table-cell-padding-left)',
    paddingRight: 'var(--table-cell-padding-right)',
    borderBottom: '1px solid rgba(13, 0, 77, 0.15)',
    position: 'relative',

    '&::after': {
      content: '""',
      position: 'absolute',
      top: 'var(--table-head-padding-top)',
      right: 0,
      width: '1px',
      height: 'calc(100% - var(--table-head-padding-top) - var(--table-head-padding-bottom))',
      backgroundColor: 'rgba(13, 0, 77, 0.15)'
    },
    '&:first-child': {
      '& > div': {
        paddingLeft: 0
      }
    },
    '& > div': {
      paddingLeft: 'var(--table-cell-gap)',
      paddingRight: 'var(--table-cell-gap)',
      alignItems: 'center',
      justifyContent: 'flex-start'
    },

    '&:last-child': {
      '& > div': {
        paddingRight: 0
      },
      '&::after': {
        content: 'none'
      }
    },
    '&.cursor': {
      cursor: 'default'
    },
    '&.MuiTableCell-stickyHeader': {
      backgroundColor: '#fff !important',
      position: 'sticky'
    },
    '&.cardThCell': {
      '&::after': {
        content: '""',
        position: 'absolute',
        top: 0,
        right: 0,
        width: '1px',
        height: 24,
        backgroundColor: 'rgba(13, 0, 77, 0.15)'
      },
      '&:last-child::after': {
        content: 'none'
      },
      '&.MuiTableCell-stickyHeader': {
        backgroundColor: `${mixins.lightThemeBgColor2} !important`,
        position: 'sticky'
      }
    }
  }
});

export const BodyTableCell = styled(TableCell)({
  '&&': {
    paddingTop: 'var(--table-cell-padding-top)',
    paddingBottom: 'var(--table-cell-padding-bottom)',
    paddingLeft: 'var(--table-cell-padding-left)',
    paddingRight: 'var(--table-cell-padding-right)',
    borderBottom: '1px solid rgba(13, 0, 77, 0.15)',
    position: 'relative',

    '&::after': {
      content: '""',
      position: 'absolute',
      top: 'var(--table-cell-padding-top)',
      right: 0,
      width: '1px',
      height: 'calc(100% - var(--table-cell-padding-top) - var(--table-cell-padding-bottom))',
      backgroundColor: 'rgba(13, 0, 77, 0.15)'
    },
    '&:first-child': {
      '& > div': {
        paddingLeft: 0
      }
    },
    '& > div': {
      paddingLeft: 'var(--table-cell-gap)',
      paddingRight: 'var(--table-cell-gap)'
    },

    '&:last-child': {
      '& > div': {
        paddingRight: 0
      },
      '&::after': {
        content: 'none'
      }
    },
    '&.tableBorder': {
      position: 'unset',

      '&::after': {
        content: 'none'
      }
    },
    '&.cardCell': {
      height: 64,
      boxSizing: 'border-box',

      '&::after': {
        content: 'none'
      }
    }
  }
});

export const TableCellContent = styled('div')({
  '&&': {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',

    '&.cardCellContent': {
      alignItems: 'center',
      minHeight: 48
    }
  }
});

export const HeaderTableCellContent = styled(TableCellContent)({
  '&&': {
    alignItems: 'center'
  }
});

export const SortIcon = styled(IconButton)({
  '&&': {
    padding: 4,
    boxSizing: 'border-box',
    color: mixins.color_text_primary,
    marginLeft: 8,
    alignSelf: 'center',

    '& svg': {
      width: 16,
      height: 16
    }
  }
});

export const ImageContainer = styled('div')({
  '&&': {
    display: 'flex',
    alignItems: 'center',
    gap: 8
  }
});

export const ImageStyled = styled('div')({
  '&&': {
    width: 32,
    height: 32,
    borderRadius: '50%',
    ...subtitle2,

    '&:has(span)': {
      borderRadius: '50%',
      border: `2px solid ${mixins.navyColor}`,
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: 12
    },
    '& svg': {
      height: '100%',
      width: '100%',
      color: mixins.navyColor
    }
  }
});

export const ImageImg = styled('img')({
  '&&': {
    width: 32,
    height: 32,
    borderRadius: '50%',
    ...subtitle2,

    '&:has(span)': {
      borderRadius: '50%',
      border: `2px solid ${mixins.navyColor}`,
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: 12
    },
    '& svg': {
      height: '100%',
      width: '100%',
      color: mixins.navyColor
    }
  }
});

export const DotIcon = styled(Icon)({
  '&&': {
    width: 6,
    height: 6,
    fontSize: 6
  }
});

export const Badge = styled(Chip)({
  '&&': {
    display: 'flex',
    padding: 0,
    borderRadius: 4,
    width: 'fit-content',
    gap: 4,
    maxHeight: 20,
    cursor: 'inherit',
    '& .MuiChip-icon': {
      fontSize: 18,
      width: 18,
      height: 18,
      marginLeft: 4,
      marginRight: -4
    },
    '& .MuiChip-label': {
      padding: '0px 8px',
      textAlign: 'center',
      fontFeatureSettings: '\'liga\' off',
      fontFamily: mixins.fontFamilyPlusSans,
      fontSize: '12px',
      fontStyle: 'normal',
      fontWeight: '500',
      lineHeight: '20px'
    }
  }
});

export const BadgeLabel = styled(Typography)({
  '&&': {
    padding: '0px 8px',
    textAlign: 'center',
    fontFeatureSettings: '\'liga\' off',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: '12px',
    fontStyle: 'normal',
    fontWeight: '500',
    lineHeight: '20px'
  }
});

export const DotIconContainer = styled('div')({
  '&&': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 18,
    height: 18
  }
});

export const Badge2 = styled('div')({
  '&&': {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '4px',
    height: '20px',
    padding: '0 4px 2px 2px',
    borderRadius: '4px',
    fontFamily: mixins.fontFamilyPlusSans,
    fontWeight: 500,
    fontSize: '12px',
    lineHeight: '16px',
    textAlign: 'center',
    verticalAlign: 'middle',
    '&.extraPadding': {
      padding: '0px 4px 0px 6px'
    }
  }
});

export const Bullet = styled('span')({
  '&&': {
    display: 'inline-block',
    width: '16px',
    height: '16px',
    lineHeight: '16px',
    textAlign: 'center',
    fontWeight: 'bold'
  }
});

export const Text = styled('div')({
  '&&': {
    fontFamily: 'Plus Jakarta Sans',
    fontSize: 12,
    fontWeight: 500,
    lineHeight: mixins.lineHeight20,
    padding: '0px 2px 0px 0px'
  }
});

export const CompletedOnText = styled(Typography)({
  '&&': {
    color: '#006632',
    fontFamily: mixins.fontFamilyPlusSans,
    fontWeight: mixins.fontWeight400,
    fontStyle: 'regular',
    fontSize: '12px',
    lineHeight: '20px'
  }
});

export const PastDue = styled(Typography)({
  '&&': {
    color: '#045CAF',
    fontFamily: mixins.fontFamilyPlusSans,
    fontWeight: mixins.fontWeight400,
    fontStyle: 'regular',
    fontSize: '12px',
    lineHeight: '20px'
  }
});

export const InfoHeaderIconContainer = styled('div')({
  '&&': {
    display: 'inline-flex',
    alignItems: 'center',
    alignSelf: 'center',
    paddingLeft: 8
  }
});

export const InfoIcon = styled('span')({
  '&&': {
    fontSize: 18,
    color: mixins.color_text_primary,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    lineHeight: 0,
    '&& .MuiSvgIcon-root': {
      fontSize: 'inherit',
      width: '1em',
      height: '1em'
    }
  }
});

export const InfoTooltip = styled('div')({
  '&&': {
    minWidth: 320
  }
});

export const InfoIconContainer = styled('div')({
  '&&': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  }
});

export const QuestionNumberWrapper = styled('div')({
  '&&': {
    display: 'flex'
  }
});

export const Operator = styled(Typography)({
  '&&': {
    color: mixins.color_text_primary,
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize16,
    fontWeight: mixins.fontWeight600,
    lineHeight: '24px',
    margin: '0px 4px'
  }
});

export const FilterConatiner = styled('div')({
  '&&': {
    display: 'flex',
    gap: 8,
    flexShrink: 0,
    marginBottom: 12,
    alignItems: 'center'
  }
});

export const FilterLabel = styled(Typography)({
  '&&': {
    color: mixins.greyScaleColor,
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize16,
    fontStyle: 'normal',
    fontWeight: mixins.fontWeight500,
    lineHeight: '18px',
    display: 'flex',
    alignItems: 'center'
  }
});

const TABLE_FILTER_INNER_WIDTH = 220;
const TABLE_FILTER_INNER_HEIGHT = 144;
const TABLE_FILTER_ITEM_HEIGHT = 48;

const TABLE_FILTER_BOX_SHADOW = '0px 5px 5px -3px rgba(0, 0, 0, 0.2), 0px 8px 10px 1px rgba(0, 0, 0, 0.14), 0px 3px 14px 2px rgba(0, 0, 0, 0.12)';

export const FilterSelect = styled(Button)({
  '&&': {
    borderRadius: 4,
    backgroundColor: mixins.chalkWhiteFont,
    border: `1px solid ${mixins.amethyst}`,
    cursor: 'pointer',
    padding: 12,
    width: 'max-content',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,

    outline: 'none',
    appearance: 'none',
    WebkitAppearance: 'none',
    '&:hover': {
      backgroundColor: mixins.chalkWhiteFont,
      border: '1px solid #8E84DC',
      boxShadow: `0px 0px 0px 4px ${mixins.amethystLightColor}`
    },

    '&:focus': {
      backgroundColor: mixins.chalkWhiteFont,
      border: `1px solid ${mixins.primaryColor}`
    },
    '&:active': {
      backgroundColor: mixins.chalkWhiteFont,
      border: `1px solid ${mixins.primaryColor}`,
      boxShadow: 'none',
      transform: 'none'
    },
    '&.filterSelectOpen': {
      borderColor: mixins.amethystColorV2
    }
  }
});

export const FilterSelectContent = styled('div')({
  '&&': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%'
  }
});

export const FilterSelectedText = styled(Typography)({
  '&&': {
    color: mixins.timeIconColor,
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize16,
    fontWeight: 600,
    lineHeight: mixins.lineHeight24,
    flex: '1 0 0',
    textTransform: 'none'
  }
});

export const FilterArrowIcon = styled('span')({
  '&&': {
    color: mixins.timeIconColor,
    fontSize: 24,
    display: 'inline-flex',
    alignItems: 'center'
  }
});

export const FilterCheckIcon = styled('span')({
  '&&': {
    display: 'inline-flex',
    alignItems: 'center',
    width: 16,
    height: 15,
    color: mixins.timeIconColor,
    '& svg': {
      width: 16,
      height: 15
    }
  }
});

export const FilterPopover = styled(Paper)({
  '&&': {
    marginTop: 8,
    borderRadius: 4,
    width: 'max-content',
    background: mixins.chalkWhiteFont,
    border: `1px solid ${mixins.amethystColorV2}`,
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)'
  }
});

export const TableFilterPopover = styled(Paper)({
  '&&': {
    boxSizing: 'content-box',
    width: TABLE_FILTER_INNER_WIDTH,
    height: TABLE_FILTER_INNER_HEIGHT,
    padding: 0,
    marginTop: 12,
    borderRadius: 4,
    background: mixins.chalkWhiteFont,
    border: `1px solid ${mixins.amethystColorV2}`,
    boxShadow: TABLE_FILTER_BOX_SHADOW,
    '--Paper-shadow': TABLE_FILTER_BOX_SHADOW,
    overflow: 'hidden'
  }
});

export const TableFilterMenuItem = styled(MenuItem)({
  '&&': {
    boxSizing: 'border-box',
    width: TABLE_FILTER_INNER_WIDTH,
    height: TABLE_FILTER_ITEM_HEIGHT,
    padding: '0 12px',
    transition: 'none !important',

    '&.Mui-selected': {
      backgroundColor: '#E8E6F3'
    },

    '&.Mui-selected:hover': {
      backgroundColor: '#E8E6F3'
    },

    '&:hover': {
      backgroundColor: '#E8E6F3'
    }
  }
});

export const FilterMenuItemContent = styled('div')({
  '&&': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    width: '100%'
  }
});

export const FilterMenuItem = styled(MenuItem)({
  '&&': {
    padding: 12,

    '&.MuiListItem-root': {
      padding: 12,
      transition: 'none !important'
    },

    '&.Mui-selected': {
      backgroundColor: '#E8E6F3'
    },

    '&.Mui-selected:hover': {
      backgroundColor: '#E8E6F3'
    },

    '&:hover': {
      backgroundColor: '#E8E6F3'
    }
  }
});

export const FilterMenuItemText = styled(Typography)({
  '&&': {
    color: mixins.timeIconColor,
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize16,
    fontWeight: 400,
    lineHeight: mixins.lineHeight24
  }
});

export const FilterMenuItemSelectedText = styled(Typography)({
  '&&': {
    color: mixins.timeIconColor,
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize16,
    fontWeight: 600,
    lineHeight: mixins.lineHeight24
  }
});


export const SelectionCount = styled('span')({
  '&&': {
    color: mixins.greyScaleColor,
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize16,
    fontStyle: 'normal',
    fontWeight: mixins.fontWeight700,
    lineHeight: '18px'
  }
});

export const SelectionLabel = styled('span')({
  '&&': {
    color: mixins.greyScaleColor,
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize16,
    fontStyle: 'normal',
    fontWeight: mixins.fontWeight400,
    lineHeight: '18px'
  }
});

export const IconButtonStyled = styled(Button)({
  '&&': {
    color: mixins.navyColor,
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize14,
    fontStyle: 'normal',
    fontWeight: mixins.fontWeight500,
    lineHeight: '22px',
    padding: 8,
    borderRadius: '1000px',
    minWidth: 'unset',
    textTransform: 'none',
    textDecoration: 'underline',

    '&:hover': {
      backgroundColor: 'transparent',
      color: mixins.linkHoverColor,
      textDecoration: 'underline'
    },
    '& > span': {
      minWidth: 'unset',
      margin: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '0px 8px 2px 8px'
    },
    '&.Mui-disabled': {
      color: mixins.disabledTextColor,
      opacity: 'unset',
      pointerEvents: 'unset',

      '&:hover': {
        background: mixins.secondaryButtonHoverColor,
        color: 'inherit'
      }
    }
  }
});

const chipSelectContainerStyles = {
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  borderRadius: 4,
  cursor: 'pointer',

  '& .MuiChip-root, & .MuiChip-label': {
    cursor: 'pointer',
    pointerEvents: 'none'
  },

  '&:hover': {
    backgroundColor: 'rgba(13, 0, 77, 0.04)'
  },

  '&.Mui-disabled': {
    cursor: 'not-allowed',
    pointerEvents: 'none'
  },

  '&:focus-visible': {
    outline: '2px solid #8E84DC',
    outlineOffset: 2
  }
};

export const ChipSelectContainer = styled('div')({
  '&&': chipSelectContainerStyles
});

/**
 * Button flavour of `ChipSelectContainer`, for the "Change to:" chip picker
 * whose whole chip + caret row is the menu trigger.
 */
export const ChipSelectButton = styled(ButtonBase)({
  '&&': chipSelectContainerStyles
});

export const DropdownTrigger = styled('div')({
  '&&': {
    display: 'inline-flex',
    alignItems: 'center',
    cursor: 'pointer',
    '&[data-disabled="true"]': {
      cursor: 'not-allowed',
      pointerEvents: 'none',
      opacity: 0.5
    }
  }
});

export const DropdownIconWrapper = styled('div')({
  '&&': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 24,
    height: 24,
    padding: 4,
    boxSizing: 'border-box'
  }
});

export const TabularSelect = styled(Select)({
  '&&': {
    '& .MuiSelect-select': {
      padding: '0px !important',
      minHeight: 'unset'
    },
    '& .MuiSelect-icon': {
      width: 24,
      height: 24,
      padding: 4,
      transition: 'transform 0.2s ease',
      '& svg': {
        width: 16,
        height: 16
      },
      '&.iconOpen': {
        transform: 'rotate(180deg)'
      }
    }
  }
});

export const TabularChipContent = styled('div')({
  '&&': {
    display: 'flex',
    alignItems: 'center',
    gap: 4
  }
});

export const ChipIcon = styled('span')({
  '&&': {
    width: 16,
    height: 16,
    transition: 'transform 200ms ease',
    '&.rotateIcon': {
      transform: 'rotate(180deg)'
    }
  }
});

export const DropdownIcon = styled('span')({
  '&&': {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 16,
    height: 16,
    transition: 'transform 200ms ease',

    '& svg': {
      width: 16,
      height: 16,
      fontSize: 16
    },

    '&.rotateIcon': {
      transform: 'rotate(180deg)'
    }
  }
});

export const ChipText = styled('span')({
  '&&': {
    display: 'inline-block',
    padding: '2px 6px'
  }
});

export const SelectionSummary = styled(Typography)({
  '&&': {
    background: '#F5F5F5',
    borderRadius: '4px',
    padding: '4px 12px'
  }
});

export const DropButton = styled(Button)({
  '&&': {
    borderRadius: '1000px',
    padding: '12px 20px',
    border: `1px solid ${mixins.amethyst}`,
    fontFamily: mixins.fontFamilyPlusSans,
    backgroundColor: mixins.amethyst,
    color: mixins.chalkWhiteFont,
    fontSize: mixins.fontSize14,
    fontWeight: mixins.fontWeight500,
    lineHeight: '24px',
    minWidth: 258,
    height: 40,
    boxSizing: 'border-box',
    textTransform: 'none',
    '&:hover': {
      backgroundColor: mixins.amethyst,
      color: mixins.chalkWhiteFont
    },
    '&.Mui-disabled': {
      backgroundColor: mixins.amethystDisabledColor,
      color: mixins.chalkWhiteFont,
      border: 'none',
      cursor: 'not-allowed'
    }
  }
});

export const TabularMenuList = styled(MenuList)({
  '&&': {
    padding: 0
  }
});

/**
 * Chip menu used by DropdownTemplate when `menuTitle` is provided
 * (roster role / status "Change to:" pickers).
 */
export const ChipMenuPaper = styled(Paper)({
  '&&': {
    marginTop: 4,
    borderRadius: 8,
    backgroundColor: mixins.chalkWhiteFont,
    // elevation/level-2
    boxShadow: `${[
      '0px 1px 14px rgba(13, 0, 77, 0.05)',
      '0px 5px 8px rgba(13, 0, 77, 0.1)',
      '0px 3px 5px -1px rgba(13, 0, 77, 0.15)'
    ].join(', ')} !important`
  }
});

export const ChipMenuList = styled(MenuList)({
  '&&': {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: 0,
    padding: '8px 0 8px'
  }
});

export const ChipMenuTrigger = styled('span')({
  '&&': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 24,
    height: 24,
    padding: 4,
    boxSizing: 'border-box',
    color: mixins.color_text_primary,

    '&:hover': {
      backgroundColor: 'rgba(13, 0, 77, 0.04)'
    }
  }
});

export const ChipMenuTitle = styled(MenuItem)({
  '&&': {
    color: mixins.color_text_primary,
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize14,
    fontWeight: mixins.fontWeight500,
    lineHeight: '16px',
    padding: '0 16px 4px',
    minHeight: 'unset',
    whiteSpace: 'nowrap',

    '&.Mui-disabled': {
      opacity: 1
    }
  }
});

export const ChipMenuItem = styled(MenuItem)({
  '&&': {
    alignSelf: 'stretch',
    width: '100%',
    minHeight: 32,
    padding: '6px 16px',
    boxSizing: 'border-box',
    backgroundColor: 'transparent',
    borderRadius: 0,

    '&&:hover': {
      backgroundColor: '#F5F5F5'
    },

    '&&.Mui-selected, &&.Mui-selected:hover': {
      backgroundColor: '#F5F5F5'
    }
  }
});

export const ChipMenuChip = styled('span')({
  '&&': {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 20,
    padding: '0px 6px 2px',
    borderRadius: 4,
    boxSizing: 'border-box',
    fontFeatureSettings: '\'liga\' off',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize12,
    fontStyle: 'normal',
    fontWeight: mixins.fontWeight500,
    lineHeight: '14px',
    textAlign: 'center'
  }
});
